import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  makeStyles,
  useTheme,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import ImageIcon from "@material-ui/icons/Image";
import VideocamIcon from "@material-ui/icons/Videocam";
import AudiotrackIcon from "@material-ui/icons/Audiotrack";
import DescriptionIcon from "@material-ui/icons/Description";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";
import GetAppIcon from "@material-ui/icons/GetApp";
import { i18n } from "../../translate/i18n";
import { getStandardButtonStyles } from "../../styles/standardButtons";

const useStyles = makeStyles((theme) => ({
  ...getStandardButtonStyles(theme),
  dialogPaper: {
    maxWidth: "540px",
    width: "100%",
    borderRadius: 16,
    backgroundColor: theme.palette.type === "dark" ? "#1E2126" : "#FFFFFF",
    border: theme.palette.type === "dark"
      ? `1px solid ${theme.palette.primary.main}40`
      : `1px solid ${theme.palette.primary.main}30`,
    boxShadow: "0 16px 48px rgba(0, 0, 0, 0.55)",
    overflow: "hidden",
  },
  dialogTitle: {
    padding: "14px 20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  dialogTitleContent: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    overflow: "hidden",
    marginRight: 8,
  },
  titleText: {
    fontSize: "1rem",
    fontWeight: 600,
    color: theme.palette.text.primary,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "400px",
  },
  closeButton: {
    padding: 4,
    color: theme.palette.text.secondary,
    "&:hover": {
      color: theme.palette.text.primary,
      backgroundColor: "transparent",
    },
  },
  dialogContent: {
    padding: 16,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: theme.palette.type === "dark" ? "#131518" : "#F7F8FA",
    minHeight: 220,
    maxHeight: 460,
  },
  imagePreview: {
    maxWidth: "100%",
    maxHeight: "380px",
    objectFit: "contain",
    borderRadius: 8,
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
  },
  videoPreview: {
    maxWidth: "100%",
    maxHeight: "380px",
    borderRadius: 8,
    outline: "none",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
  },
  audioContainer: {
    width: "100%",
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 16,
  },
  audioPlayer: {
    width: "100%",
    outline: "none",
  },
  documentContainer: {
    width: "100%",
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
    textAlign: "center",
  },
  dialogActions: {
    padding: "12px 20px",
    borderTop: `1px solid ${theme.palette.divider}`,
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
  },
}));

const MediaPreviewModal = ({ open, onClose, media }) => {
  const classes = useStyles();
  const theme = useTheme();

  if (!media) return null;

  const url = media.url || "";
  const type = media.type || "images";
  const filename = media.name || url.split("/").pop() || i18n.t("contactDrawer.previewTitle");

  const isPdf = url.toLowerCase().includes(".pdf") || (media.name && media.name.toLowerCase().endsWith(".pdf"));

  const renderIcon = () => {
    switch (type) {
      case "images":
        return <ImageIcon style={{ color: theme.palette.primary.main, fontSize: 22 }} />;
      case "videos":
        return <VideocamIcon style={{ color: theme.palette.primary.main, fontSize: 22 }} />;
      case "audios":
        return <AudiotrackIcon style={{ color: theme.palette.primary.main, fontSize: 22 }} />;
      case "documents":
        return isPdf ? (
          <DescriptionIcon style={{ color: theme.palette.primary.main, fontSize: 22 }} />
        ) : (
          <InsertDriveFileIcon style={{ color: theme.palette.primary.main, fontSize: 22 }} />
        );
      default:
        return <InsertDriveFileIcon style={{ color: theme.palette.primary.main, fontSize: 22 }} />;
    }
  };

  const renderContent = () => {
    if (type === "images") {
      return (
        <img
          src={url}
          alt={filename}
          className={classes.imagePreview}
          onError={(e) => {
            e.target.onerror = null;
            e.target.style.display = "none";
          }}
        />
      );
    }

    if (type === "videos") {
      return (
        <video
          src={url}
          controls
          autoPlay
          className={classes.videoPreview}
        />
      );
    }

    if (type === "audios") {
      return (
        <Box className={classes.audioContainer}>
          <AudiotrackIcon style={{ fontSize: 56, color: theme.palette.primary.main, opacity: 0.85 }} />
          <Typography variant="body2" color="textSecondary" style={{ maxWidth: 360, wordBreak: "break-all" }}>
            {filename}
          </Typography>
          <audio src={url} controls autoPlay className={classes.audioPlayer} />
        </Box>
      );
    }

    if (type === "documents" && isPdf) {
      return (
        <iframe
          src={url}
          title={filename}
          style={{
            width: "100%",
            height: "380px",
            border: "none",
            borderRadius: 8,
          }}
        />
      );
    }

    // Cualquier otro documento (Word, Excel, Zip, etc.)
    return (
      <Box className={classes.documentContainer}>
        <InsertDriveFileIcon style={{ fontSize: 64, color: theme.palette.primary.main }} />
        <Typography variant="subtitle2" style={{ fontWeight: 600, maxWidth: 360, wordBreak: "break-all" }}>
          {filename}
        </Typography>
        <Typography variant="caption" color="textSecondary" style={{ maxWidth: 380 }}>
          {i18n.t("contactDrawer.documentPreview")}
        </Typography>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <div className={classes.dialogTitleContent}>
          {renderIcon()}
          <span className={classes.titleText}>{filename}</span>
        </div>
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        {renderContent()}
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button
          onClick={onClose}
          className={classes.dangerButton}
          startIcon={<CloseIcon fontSize="small" />}
          style={{ borderRadius: 8, textTransform: "capitalize" }}
        >
          {i18n.t("contactDrawer.close")}
        </Button>
        {url && (
          <Button
            component="a"
            href={url}
            download={filename}
            target="_blank"
            rel="noopener noreferrer"
            className={classes.applyButton}
            startIcon={<GetAppIcon fontSize="small" />}
            style={{ borderRadius: 8, textTransform: "capitalize" }}
          >
            {i18n.t("contactDrawer.download")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default MediaPreviewModal;
